/**
 * MERCURY_FAIRWAY — region catalogue.
 *
 * Shared by the app (region picker, handicap-ID validation) and the Worker
 * (provider routing). Single source of truth: if a region is added here it
 * appears in the mobile app AND gets routed server side.
 *
 * The important structural fact: DotGolf (50% R&A, 50% Golf New Zealand) runs
 * the national handicapping platform for Australia, New Zealand, England,
 * Scotland, Wales and Ireland. One provider adapter therefore covers six
 * countries. The USA is separate (GHIN/USGA), as is Canada and South Africa.
 *
 * Every one of these is credential-gated. None can be called anonymously.
 */

export type ProviderId = 'dotgolf' | 'golflink' | 'ghin' | 'golfcanada' | 'golfrsa' | 'none';

export interface Region {
  /** ISO 3166-1 alpha-2, the value the mobile app stores. */
  code: string;
  country: string;
  /** National association that owns the handicap database. */
  body: string;
  /** Platform behind that association. */
  platform: string;
  provider: ProviderId;
  /** What the golfer's ID is called in that country — shown on the input. */
  idLabel: string;
  /** Validation for that ID. Kept deliberately loose: rejecting a valid golfer
   *  is worse than accepting one the federation will bounce. */
  idPattern: string;
  idExample: string;
  /** Default distance unit golfers in that region expect. */
  unit: 'm' | 'yd';
  /** Whether the platform supports third-party score posting at all. */
  canPostScores: boolean;
  /** What the operator must obtain, and from whom. */
  credentials: string;
}

export const REGIONS: Region[] = [
  {
    code: 'AU',
    country: 'Australia',
    body: 'Golf Australia',
    platform: 'Golf Australia CONNECT / GOLF Link (DotGolf)',
    provider: 'golflink',
    idLabel: 'GOLF Link number',
    idPattern: '^\\d{6,12}$',
    idExample: '2011234567',
    unit: 'm',
    canPostScores: true,
    credentials: 'Club ID + DES Key, issued by GOLF Link to the club',
  },
  {
    code: 'NZ',
    country: 'New Zealand',
    body: 'Golf New Zealand',
    platform: 'DotGolf',
    provider: 'dotgolf',
    idLabel: 'DotGolf number',
    idPattern: '^\\d{6,9}$',
    idExample: '1234567',
    unit: 'm',
    canPostScores: true,
    credentials: 'DotGolf partner credentials via Golf New Zealand',
  },
  {
    code: 'GB',
    country: 'England',
    body: 'England Golf',
    platform: 'DotGolf / WHS',
    provider: 'dotgolf',
    idLabel: 'England Golf / CDH number',
    idPattern: '^\\d{6,12}$',
    idExample: '123456789',
    unit: 'yd',
    canPostScores: true,
    credentials: 'DotGolf partner credentials via England Golf',
  },
  {
    code: 'SC',
    country: 'Scotland',
    body: 'Scottish Golf',
    platform: 'DotGolf / WHS',
    provider: 'dotgolf',
    idLabel: 'CDH number',
    idPattern: '^\\d{6,12}$',
    idExample: '123456789',
    unit: 'yd',
    canPostScores: true,
    credentials: 'DotGolf partner credentials via Scottish Golf',
  },
  {
    code: 'WA',
    country: 'Wales',
    body: 'Wales Golf',
    platform: 'DotGolf / WHS',
    provider: 'dotgolf',
    idLabel: 'CDH number',
    idPattern: '^\\d{6,12}$',
    idExample: '123456789',
    unit: 'yd',
    canPostScores: true,
    credentials: 'DotGolf partner credentials via Wales Golf',
  },
  {
    code: 'IE',
    country: 'Ireland',
    body: 'Golf Ireland',
    platform: 'DotGolf / WHS',
    provider: 'dotgolf',
    idLabel: 'Golf Ireland number',
    idPattern: '^\\d{6,12}$',
    idExample: '123456789',
    unit: 'm',
    canPostScores: true,
    credentials: 'DotGolf partner credentials via Golf Ireland',
  },
  {
    code: 'US',
    country: 'United States',
    body: 'USGA',
    platform: 'GHIN',
    provider: 'ghin',
    idLabel: 'GHIN number',
    idPattern: '^\\d{7,10}$',
    idExample: '1234567',
    unit: 'yd',
    canPostScores: true,
    credentials: 'GHIN API key from USGA or the state association — fees apply',
  },
  {
    code: 'CA',
    country: 'Canada',
    body: 'Golf Canada',
    platform: 'Golf Canada Score Centre',
    provider: 'golfcanada',
    idLabel: 'Golf Canada number',
    idPattern: '^\\d{6,12}$',
    idExample: '12345678',
    unit: 'yd',
    canPostScores: true,
    credentials: 'Golf Canada partner agreement',
  },
  {
    code: 'ZA',
    country: 'South Africa',
    body: 'GolfRSA',
    platform: 'HNA / WHS',
    provider: 'golfrsa',
    idLabel: 'GolfRSA number',
    idPattern: '^\\d{6,12}$',
    idExample: '1234567',
    unit: 'm',
    canPostScores: true,
    credentials: 'GolfRSA partner agreement',
  },
  {
    code: 'XX',
    country: 'Other / social play',
    body: 'None',
    platform: 'Local only',
    provider: 'none',
    idLabel: 'Player reference (optional)',
    idPattern: '^.{0,32}$',
    idExample: '',
    unit: 'm',
    canPostScores: false,
    credentials: 'None — scores stay in MERCURY_FAIRWAY',
  },
];

export const DEFAULT_REGION = 'AU';

export function getRegion(code: string): Region {
  return REGIONS.find((r) => r.code === code) ?? REGIONS[0];
}

export function validateHandicapId(code: string, id: string): { ok: boolean; message?: string } {
  const region = getRegion(code);
  const trimmed = id.trim();
  if (!trimmed) {
    return region.provider === 'none'
      ? { ok: true }
      : { ok: false, message: `${region.idLabel} is required to post scores to ${region.body}.` };
  }
  if (!new RegExp(region.idPattern).test(trimmed)) {
    return {
      ok: false,
      message: `That does not look like a ${region.idLabel} (example: ${region.idExample}).`,
    };
  }
  return { ok: true };
}
